import { ProgramEvent } from "@cue/api";
import { LoadingSpinner } from "@cue/atoms";
import { useHash, useQueryParams } from "@cue/hooks";
import { DebugMenu, LocalStorageUtil } from "@cue/utility";
import { Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { CMS_Content_Module } from "@project/cms/types";
import { useOgTags } from "@project/hooks/use-og-tags";
import { useUser } from "@project/hooks/use-user";
import { Consent } from "@project/site/core/consent/consent";
import { Footer } from "@project/site/layout/footer";
import { Header } from "@project/site/layout/header";
import { useLocation } from "@reach/router";
import { navigate } from "gatsby";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { Helmet } from "react-helmet";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
let analytics_to;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare global {
	interface Window {
		ste_statistic: unknown[];
	}
}
export const PageFrame: React.FC<PageFrameProps> = (props) => {
	const {
		children,
		path,
		pageName,
		pageContext: { lang, isFrontPage, globalOptions},
		perfectScroll = true,
	} = props;
	const location = useLocation();

	const [params] = useQueryParams({
		location,
	});

	const { hash } = useHash();

	const [currentHash, setCurrentHash] = useState(
		typeof window !== "undefined"
			? document.location.hash.replace("#", "")
			: "",
	);

	const { setDefault, loaded: ogTagsLoaded } = useOgTags();
	const [isStage, setIsStage] = useState(false);
	const [canTrack, setCanTrack] = useState(false);
	const [scrollbarLoaded, setScrollbarLoaded] = useState(false);

	useEffect(() => {
		setDefault &&
			setDefault({
				title: Array.isArray(globalOptions?.seo_page_title)
					? globalOptions?.seo_page_title?.find(
							({ language }) => language === lang,
						)?.value || undefined
					: undefined,
				description: Array.isArray(globalOptions?.seo_page_description)
					? globalOptions?.seo_page_description?.find(
							({ language }) => language === lang,
						)?.value || undefined
					: undefined,
				image: Array.isArray(globalOptions?.seo_page_image)
					? globalOptions?.seo_page_image?.find(
							({ language }) => language === lang,
						)?.value || undefined
					: undefined,
				image_width: 1280,
				image_height: 720,
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ogTagsLoaded]);

	const [isScrolled, setIsScrolled] = useState(false);

	const footerRef = useRef<HTMLElement>(null);
	const trackPageInit = () => {
		try {
			const path = document.location.pathname; //document.location.href.replace(document.location.origin, '');
			console.debug("TRACKING", path, {
				action: "page.ready",
				data: {
					page: {
						country:"WW",
						path: path,
						language: lang,
					},
				},
			});

			window.ste_statistic = window.ste_statistic || [];
			window.ste_statistic.push({
				action: "page.ready",
				data: {
					page: {
						country:"WW",
						path: path,
						language: lang,
					},
				},
			});
		} catch (error) {
			console.warn("Tracking fail");
		}
	};

	const pageReadyEvent = () => {
		trackPageInit();
	};

	useEffect(() => {
		const handleScroll = () => {
			// Check if the page is scrolled
			if (window.scrollY > 0) {
				setIsScrolled(true);
			} else {
				setIsScrolled(false);
			}

			if (
				footerRef?.current &&
				window.innerHeight + window.scrollY >=
					document.body.offsetHeight - footerRef?.current.clientHeight / 2
			) {
				document.body.classList.add("sticky-footer");
			} else {
				document.body.classList.remove("sticky-footer");
			}
		};

		// Attach the scroll event listener when the component mounts
		window.addEventListener("scroll", handleScroll);

		// Clean up the event listener when the component unmounts
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	useEffect(() => {
		setTimeout(() => {
			// SCROLL INTO VIEW
			const el = document?.getElementById(`${currentHash}`);

			if (el && !params?.uid) {
				window.scrollTo({
					behavior: "smooth",
					top:
						el.getBoundingClientRect().top -
						document.body.getBoundingClientRect().top,
				});
			}
		}, 800);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		document.body.setAttribute("data-page-name", pageName || "");

		if (lang === "") {
			navigate("/en");
		}
	}, [pageName, lang]);

	useEffect(() => setIsStage(document.location.host.includes("cuenect")), []);

	useEffect(() => {
		async function trackingInit() {
			pageReadyEvent();
		}
		trackingInit();

		window.addEventListener("UC_SDK_EVENT", async () => {
			const consent = await Consent.areAllConsentsAccepted();
			console.log("UC_SDK UC_SDK_EVENT", consent);
			setCanTrack(consent);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { isAuthenticated } = useUser();

	useEffect(() => {
		const isOnsite = (LocalStorageUtil.get('onSiteMode') as boolean) || false;
		if(path==="/en/" || path==="/de/"){
			if(isOnsite && props?.pageContext?.pageInfo?.onsite){ 
				window.location.href =`${props?.pageContext?.pageInfo?.onsite}${window.location.search || ""}`
			}
		}
	}, [])
	

	//const isAuthenticated = false;
	const content = (
		<>
			{isAuthenticated === undefined ? (
				<div className="cue-project-loading">
					<LoadingSpinner />
				</div>
			) : (
				<Header
					lang={lang}
					isFrontPage={isFrontPage}
					className={isScrolled ? "scrolled" : ""}
					path={path || ""}
				/>
			)}

			<ContentContainer
				className={`cue-project-content-container ${scrollbarLoaded ? "loaded" : ""}`}
			>
				{children}
			</ContentContainer>
			<Footer path={path || ""} footerRef={footerRef} />
		</>
	);

	return (
		<>
			<Helmet>
				<script src="//w3.siemens.com/ote/ote_config.js"></script>
				<script src="//w3.siemens.com/ote/global/ote.js"></script>
			</Helmet>
			<PageFrameContainer>
				{isStage && !isMobile && (
					<div className="cue-stage-banner">Stage Version</div>
				)}
				<DebugMenu />

				<div className="cue-project-wrapper">
					{perfectScroll ? (
						<PerfectScrollbar
							id="perfect-scroller"
							containerRef={() => {
								console.log("SCROLLBAR LOADED");
								setScrollbarLoaded(true);

								// setScrollKey(ref);
								// onScrollerInit && onScrollerInit(ref);
							}}
							onScrollY={() => {
								// onYScroll && onYScroll(container);
								//if (scrollYSuppressed) {
								// container.scrollTop = 0;
								//}
							}}
						>
							{content}
						</PerfectScrollbar>
					) : (
						<>{content}</>
					)}
				</div>
			</PageFrameContainer>
		</>
	);
};

export interface PageFrameProps extends PageParams {
	children?: ReactNode;
	pageName?: string;
	theme?: Theme;
	noFrame?: boolean;
	perfectScroll?: boolean | null;
}
export type PageInfo = {
	title: string;
	meta_description: string | null;
	redirect: string | null;
	content?: CMS_Content_Module[];
	onsite: string | null;
	offsite: string | null;
};
export interface PageParams<T = undefined> {
	pageContext: {
		lang: string;
		item?: string;
		program?: string;
		hidePostEvent?: boolean;
		uid: string; //For informatierial
		pageInfo: PageInfo;
		isPublic: boolean;
		isFrontPage?: boolean;
		globalOptions: Record<
			string,
			string | { language: string; value: string }[]
		>;
		event?: ProgramEvent;
	};
	path?: string;
	isTop?: boolean;
	onScrollerInit: (ref: HTMLElement) => void;
	location?: Location;
	data?: T;
}

const PageFrameContainer = styled.div``;
const ContentContainer = styled.div``;
